import { useQuery } from '@tanstack/react-query';
import { Fragment, useMemo, useState } from 'react';
import { Avatar, Badge, Empty, Field, Skeleton, Stat } from '../components/ui';
import { api, type ReportUser, type TeamMember } from '../lib/api';
import { daysAgoRange, hhmm, hm, monthRange, prettyDate, shiftMonth, todayKey } from '../lib/format';
import { useAuth } from '../lib/auth';

type Preset = 'this-month' | 'last-month' | 'last-7' | 'last-30' | 'custom';

export function ReportsPage() {
  const { me } = useAuth();
  const isAdmin = me?.role === 'ADMIN';

  const [preset, setPreset] = useState<Preset>('this-month');
  const [range, setRange] = useState(() => monthRange());
  const [target, setTarget] = useState<string>('me');
  const [showEmpty, setShowEmpty] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);

  const { data: team } = useQuery({
    queryKey: ['team'],
    queryFn: () => api.get<TeamMember[]>('/admin/users'),
    enabled: isAdmin,
    staleTime: 60_000,
  });

  const query = useMemo(() => {
    const p = new URLSearchParams({ from: range.from, to: range.to, userId: target });
    if (showEmpty) p.set('includeEmptyDays', 'true');
    return p.toString();
  }, [range, target, showEmpty]);

  const { data, isFetching } = useQuery({
    queryKey: ['report', query],
    queryFn: () => api.get<{ from: string; to: string; users: ReportUser[] }>(`/reports?${query}`),
    placeholderData: (prev) => prev,
  });

  function applyPreset(p: Preset) {
    setPreset(p);
    if (p === 'this-month') setRange(monthRange());
    else if (p === 'last-month') setRange(shiftMonth(monthRange().from, -1));
    else if (p === 'last-7') setRange(daysAgoRange(7));
    else if (p === 'last-30') setRange(daysAgoRange(30));
  }

  const users = data?.users ?? [];
  const grand = users.reduce(
    (a, u) => ({
      worked: a.worked + u.totals.workedMinutes + u.totals.creditMinutes,
      target: a.target + u.totals.targetMinutes,
      balance: a.balance + u.totals.balanceMinutes,
      days: a.days + u.totals.daysWorked,
    }),
    { worked: 0, target: 0, balance: 0, days: 0 },
  );

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Relatório de horas</h1>
          <p>
            {prettyDate(range.from)} a {prettyDate(range.to)}
            {isFetching ? ' · atualizando…' : ''}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {/* navegação de verdade para o browser tratar o Content-Disposition */}
          <a className="btn btn--sm" href={`/api/reports/export.pdf?${query}`} download>
            Exportar PDF
          </a>
          <a className="btn btn--sm" href={`/api/reports/export.csv?${query}`} download>
            Exportar CSV
          </a>
        </div>
      </div>

      <section className="card" style={{ marginBottom: 18 }}>
        <div className="card__body" style={{ display: 'grid', gap: 16 }}>
          <div className="presets">
            {(
              [
                ['this-month', 'Este mês'],
                ['last-month', 'Mês passado'],
                ['last-7', 'Últimos 7 dias'],
                ['last-30', 'Últimos 30 dias'],
                ['custom', 'Personalizado'],
              ] as const
            ).map(([value, label]) => (
              <button
                key={value}
                className="preset"
                aria-pressed={preset === value}
                onClick={() => applyPreset(value)}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="form-row">
            <Field label="De">
              <input
                type="date"
                value={range.from}
                max={range.to}
                onChange={(e) => {
                  setPreset('custom');
                  setRange((r) => ({ ...r, from: e.target.value }));
                }}
              />
            </Field>
            <Field label="Até">
              <input
                type="date"
                value={range.to}
                min={range.from}
                max={todayKey()}
                onChange={(e) => {
                  setPreset('custom');
                  setRange((r) => ({ ...r, to: e.target.value }));
                }}
              />
            </Field>

            {isAdmin ? (
              <Field label="Estagiário">
                <select value={target} onChange={(e) => setTarget(e.target.value)}>
                  <option value="me">Eu mesmo</option>
                  <option value="all">Todos os ativos</option>
                  {(team ?? [])
                    .filter((t) => t.id !== me?.id)
                    .map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                </select>
              </Field>
            ) : null}

            <Field label="Dias sem lançamento">
              <select
                value={showEmpty ? 'yes' : 'no'}
                onChange={(e) => setShowEmpty(e.target.value === 'yes')}
              >
                <option value="no">Ocultar</option>
                <option value="yes">Mostrar (achar faltas)</option>
              </select>
            </Field>
          </div>
        </div>
      </section>

      {users.length > 1 ? (
        <div className="grid grid--stats" style={{ marginBottom: 18 }}>
          <Stat label="Total do período" value={hm(grand.worked)} hint={`${users.length} pessoas`} />
          <Stat label="Meta somada" value={hm(grand.target)} />
          <Stat
            label="Saldo"
            value={hm(grand.balance)}
            tone={grand.balance >= 0 ? 'pos' : 'neg'}
          />
          <Stat label="Dias com lançamento" value={String(grand.days)} />
        </div>
      ) : null}

      {isFetching && !data ? (
        <div className="card">
          <div className="card__body" style={{ display: 'grid', gap: 10 }}>
            <Skeleton height={28} />
            <Skeleton height={200} />
          </div>
        </div>
      ) : users.length === 0 ? (
        <div className="card">
          <Empty
            title="Nenhum lançamento no período"
            hint="Ajuste as datas ou mostre os dias sem lançamento."
          />
        </div>
      ) : (
        users.map((u) => (
          <UserReport
            key={u.userId}
            user={u}
            pdfHref={`/api/reports/export.pdf?${pdfQuery(query, u.userId)}`}
            expanded={expanded}
            onToggle={(k) => setExpanded(expanded === k ? null : k)}
          />
        ))
      )}
    </>
  );
}

/** Mesma consulta da tela, restrita a um funcionário: o PDF individual. */
function pdfQuery(query: string, userId: string): string {
  const p = new URLSearchParams(query);
  p.set('userId', userId);
  return p.toString();
}

function UserReport({
  user,
  pdfHref,
  expanded,
  onToggle,
}: {
  user: ReportUser;
  pdfHref: string;
  expanded: string | null;
  onToggle: (key: string) => void;
}) {
  return (
    <section className="card" style={{ marginBottom: 18 }}>
      <div className="card__head">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Avatar name={user.name} src={user.avatarUrl} />
          <div>
            <h2>{user.name}</h2>
            <div style={{ fontSize: '0.8rem', color: 'var(--faint)' }}>{user.email}</div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Badge tone={user.totals.balanceMinutes >= 0 ? 'ok' : 'danger'}>
            {hm(user.totals.workedMinutes + user.totals.creditMinutes)} · saldo{' '}
            {hm(user.totals.balanceMinutes)}
          </Badge>
          <a className="btn btn--ghost btn--sm" href={pdfHref} download title="PDF deste funcionário">
            PDF
          </a>
        </div>
      </div>

      <div className="card__body" style={{ paddingBottom: 0 }}>
        <div className="grid grid--stats">
          <Stat
            label="Trabalhadas"
            value={hm(user.totals.workedMinutes)}
            hint={user.totals.creditMinutes ? `+ ${hm(user.totals.creditMinutes)} de abono` : undefined}
          />
          <Stat
            label="Meta"
            value={hm(user.totals.targetMinutes)}
            hint={`${user.totals.expectedDays} dias úteis`}
          />
          <Stat
            label="Saldo"
            value={hm(user.totals.balanceMinutes)}
            tone={user.totals.balanceMinutes >= 0 ? 'pos' : 'neg'}
          />
          <Stat
            label="Média por dia"
            value={hm(user.totals.averageMinutesPerWorkedDay)}
            hint={`${user.totals.daysWorked} dias com registro`}
          />
        </div>
      </div>

      <div className="table-wrap" style={{ marginTop: 16 }}>
        <table>
          <thead>
            <tr>
              <th>Dia</th>
              <th>Entrada</th>
              <th>Saída</th>
              <th className="num">Trabalhado</th>
              <th className="num">Abono</th>
              <th className="num">Pausas</th>
              <th className="num">Saldo</th>
              <th>Atividade</th>
            </tr>
          </thead>
          <tbody>
            {user.days.map((d) => {
              const key = `${user.userId}|${d.workDate}`;
              const missing =
                d.expected && d.workedMinutes === 0 && d.creditMinutes === 0 && !d.openNow;
              return (
                <Fragment key={key}>
                  <tr
                    className={missing ? 'is-missing' : !d.expected ? 'is-weekend' : undefined}
                    onClick={() => onToggle(key)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td style={{ whiteSpace: 'nowrap', fontWeight: 600 }}>
                      {prettyDate(d.workDate)}
                      {d.openNow ? (
                        <>
                          {' '}
                          <Badge tone="ok">em aberto</Badge>
                        </>
                      ) : null}
                      {missing ? (
                        <>
                          {' '}
                          <Badge tone="danger">sem registro</Badge>
                        </>
                      ) : null}
                      {d.holiday ? (
                        <>
                          {' '}
                          <Badge tone="info">feriado</Badge>
                        </>
                      ) : null}
                    </td>
                    <td className="tnum">{d.firstIn ?? '—'}</td>
                    <td className="tnum">{d.lastOut ?? '—'}</td>
                    <td className="num" style={{ fontWeight: 700 }}>
                      {hhmm(d.workedMinutes)}
                    </td>
                    <td className="num" title={d.creditReason ?? undefined}>
                      {d.creditMinutes ? hhmm(d.creditMinutes) : '—'}
                    </td>
                    <td className="num">{d.breakMinutes ? hhmm(d.breakMinutes) : '—'}</td>
                    <td
                      className="num"
                      style={{
                        color: d.balanceMinutes >= 0 ? 'var(--ok)' : 'var(--danger)',
                        fontWeight: 700,
                      }}
                    >
                      {d.expected || d.workedMinutes ? hhmm(d.balanceMinutes) : '—'}
                    </td>
                    <td style={{ maxWidth: 320, color: 'var(--muted-2)' }}>
                      {!d.note && (d.creditReason || d.holiday) ? (
                        d.creditReason ?? d.holiday
                      ) : d.note ? (
                        <span
                          style={{
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {d.note}
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>

                  {expanded === key ? (
                    <tr>
                      <td colSpan={8} style={{ background: 'var(--surface-2)' }}>
                        <strong style={{ fontSize: '0.78rem', letterSpacing: '0.06em' }}>
                          LANÇAMENTOS DO DIA
                        </strong>
                        <ul style={{ margin: '10px 0 0', paddingLeft: 18, lineHeight: 1.9 }}>
                          {d.segments.map((s) => (
                            <li key={s.id}>
                              <span className="tnum" style={{ fontWeight: 700 }}>
                                {s.start} → {s.end ?? 'em aberto'}
                              </span>{' '}
                              <span style={{ color: 'var(--muted-2)' }}>
                                {s.kind === 'BREAK' ? 'pausa' : 'trabalho'}
                                {s.minutes !== null ? ` · ${hm(s.minutes)}` : ''}
                                {s.version > 1 ? ` · ${s.version - 1} edição(ões)` : ''}
                                {s.description ? ` · ${s.description}` : ''}
                              </span>
                            </li>
                          ))}
                        </ul>
                        {d.note ? (
                          <p style={{ marginTop: 12, color: 'var(--muted)' }}>{d.note}</p>
                        ) : null}
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
